/**
 * Error handling module for the application.
 *
 * This module defines a set of custom error classes that represent different error
 * conditions that can occur within the application. These error classes are designed
 * to be caught and handled appropriately at different layers of the application.
 *
 * The error hierarchy is structured to provide clear categorization of errors:
 * - Resource errors (not found, validation failures)
 * - Authentication errors (invalid credentials, insufficient permissions)
 * - System errors (database failures, unexpected conditions)
 *
 * Each error includes:
 * - A unique name for instanceof checks
 * - A descriptive message for logging and debugging
 * - A consistent structure for handling in resolvers and middleware
 *
 * @module errors
 */

/**
 * Base application error class that all other error types extend.
 *
 * Provides common functionality and structure for all application errors,
 * including error codes and standardized formatting for GraphQL responses.
 */
export class ApplicationError extends Error {
  /**
   * Error code that can be used for client-side error handling.
   */
  code: string;

  /**
   * Creates a new ApplicationError instance.
   *
   * @param {string} message - Human-readable error description
   * @param {string} code - Error code for programmatic handling
   */
  constructor(message: string, code: string) {
    super(message);
    this.name = "ApplicationError";
    this.code = code;
  }

  /**
   * Converts the error to a GraphQL-compatible format.
   *
   * @returns {Object} GraphQL formatted error object
   */
  toGraphQLError() {
    return {
      message: this.message,
      extensions: {
        code: this.code,
        errorType: this.name,
      },
    };
  }
}

/**
 * ErrorNotFound represents an error condition when a requested resource cannot be found.
 *
 * This custom error class extends the ApplicationError class and is specifically
 * designed for handling "not found" scenarios in the application's resource stores.
 * It provides a consistent way to indicate that a requested entity (such as a Sequence,
 * Registry, or Invoice) doesn't exist in the data store.
 *
 * ## Common Usage Patterns
 * This error should be thrown whenever a lookup operation fails to find a requested
 * resource. It's particularly useful in resource store methods when:
 * - Fetching a resource by ID and the resource doesn't exist
 * - Looking up related components of a resource that are missing
 * - Validating references to other resources before performing operations
 *
 * ## Error Handling Strategy
 * When working with resource stores, implement a consistent error handling pattern:
 * 1. Catch ErrorNotFound at the resolver layer
 * 2. Convert to appropriate GraphQL error or null return value
 * 3. Log relevant information for debugging purposes
 * 4. Avoid exposing internal identifiers in user-facing error messages
 *
 * @example
 * // Throwing an ErrorNotFound in a resource method
 * static async get(id: string): Promise<Resource> {
 *   const resource = await kv.get(['resources', id]);
 *   if (!resource.value) {
 *     throw new ErrorNotFound(`Resource not found: ${id}`);
 *   }
 *   return resource.value;
 * }
 *
 * @example
 * // Handling an ErrorNotFound in a resolver
 * try {
 *   const resource = await ResourceStore.get(id);
 *   return resource;
 * } catch (error) {
 *   if (error instanceof ErrorNotFound) {
 *     // Handle not found case specifically
 *     console.log(`Resource lookup failed: ${error.message}`);
 *     return null;
 *   }
 *   // Re-throw other types of errors
 *   throw error;
 * }
 */
export class ErrorNotFound extends ApplicationError {
  /**
   * Creates a new ErrorNotFound instance.
   *
   * @param {string} message - Descriptive error message explaining what resource
   *                           was not found and any relevant identifying information.
   *                           Best practice is to include the resource type and ID.
   */
  constructor(message: string) {
    super(message, "RESOURCE_NOT_FOUND");
    this.name = "ErrorNotFound";
  }
}

/**
 * ErrorValidation represents an error condition when input validation fails.
 *
 * This error is used when user-provided data fails validation rules before
 * processing. It includes details about specific validation failures to help
 * clients correct their input.
 */
export class ErrorValidation extends ApplicationError {
  /**
   * Specific validation errors mapped by field name.
   */
  fieldErrors: Record<string, string>;

  /**
   * Creates a new ErrorValidation instance.
   *
   * @param {string} message - General validation error message
   * @param {Record<string, string>} fieldErrors - Map of field names to specific error messages
   */
  constructor(message: string, fieldErrors: Record<string, string> = {}) {
    super(message, "VALIDATION_ERROR");
    this.name = "ErrorValidation";
    this.fieldErrors = fieldErrors;
  }

  /**
   * Converts the error to a GraphQL-compatible format with field error details.
   *
   * @returns {Object} GraphQL formatted error object
   */
  override toGraphQLError() {
    return {
      message: this.message,
      extensions: {
        code: this.code,
        errorType: this.name,
        fieldErrors: this.fieldErrors,
      },
    };
  }
}

/**
 * ErrorAuthentication represents authentication failures.
 *
 * Used when a request lacks valid authentication credentials or
 * when the provided credentials are invalid or expired.
 */
export class ErrorAuthentication extends ApplicationError {
  /**
   * Creates a new ErrorAuthentication instance.
   *
   * @param {string} message - Authentication error details
   */
  constructor(message: string) {
    super(message, "AUTHENTICATION_ERROR");
    this.name = "ErrorAuthentication";
  }
}

/**
 * ErrorAuthorization represents authorization failures.
 *
 * Used when an authenticated user attempts to access a resource
 * or perform an action they don't have permission for.
 */
export class ErrorAuthorization extends ApplicationError {
  /**
   * Required scopes that were missing from the request.
   */
  requiredScopes: string[];

  /**
   * Creates a new ErrorAuthorization instance.
   *
   * @param {string} message - Authorization error details
   * @param {string[]} requiredScopes - Scopes required for the operation
   */
  constructor(message: string, requiredScopes: string[] = []) {
    super(message, "AUTHORIZATION_ERROR");
    this.name = "ErrorAuthorization";
    this.requiredScopes = requiredScopes;
  }

  /**
   * Converts the error to a GraphQL-compatible format with scope requirements.
   *
   * @returns {Object} GraphQL formatted error object
   */
  override toGraphQLError() {
    return {
      message: this.message,
      extensions: {
        code: this.code,
        errorType: this.name,
        requiredScopes: this.requiredScopes,
      },
    };
  }
}

/**
 * ErrorDatabase represents database operation failures.
 *
 * Used for errors related to Deno KV operations, such as
 * connection issues, transaction failures, or data integrity problems.
 */
export class ErrorDatabase extends ApplicationError {
  /**
   * Indicates whether the error is transient and the operation could be retried.
   */
  isTransient: boolean;

  /**
   * Creates a new ErrorDatabase instance.
   *
   * @param {string} message - Database error details
   * @param {boolean} isTransient - Whether the error is transient and can be retried
   */
  constructor(message: string, isTransient = false) {
    super(message, "DATABASE_ERROR");
    this.name = "ErrorDatabase";
    this.isTransient = isTransient;
  }

  /**
   * Converts the error to a GraphQL-compatible format with retry information.
   *
   * @returns {Object} GraphQL formatted error object
   */
  override toGraphQLError() {
    return {
      message: this.message,
      extensions: {
        code: this.code,
        errorType: this.name,
        isTransient: this.isTransient,
      },
    };
  }
}

/**
 * ErrorRateLimited represents a rate limit exceeded condition.
 *
 * Used when a client has exceeded their allowed rate limit for a specific
 * operation. Includes information about when they can retry.
 */
export class ErrorRateLimited extends ApplicationError {
  /**
   * Number of tokens remaining (should be 0 when rate limited).
   */
  remaining: number;

  /**
   * Maximum tokens allowed in the bucket.
   */
  limit: number;

  /**
   * Milliseconds until the client can retry.
   */
  retryAfterMs: number;

  /**
   * Creates a new ErrorRateLimited instance.
   *
   * @param {string} message - Rate limit error details
   * @param {object} options - Rate limit status information
   * @param {number} options.remaining - Tokens remaining
   * @param {number} options.limit - Maximum tokens allowed
   * @param {number} options.retryAfterMs - Milliseconds until retry allowed
   */
  constructor(message: string, options: { remaining: number; limit: number; retryAfterMs: number }) {
    super(message, "RATE_LIMITED");
    this.name = "ErrorRateLimited";
    this.remaining = options.remaining;
    this.limit = options.limit;
    this.retryAfterMs = options.retryAfterMs;
  }

  /**
   * Converts the error to a GraphQL-compatible format with rate limit information.
   *
   * @returns {Object} GraphQL formatted error object
   */
  override toGraphQLError() {
    return {
      message: this.message,
      extensions: {
        code: this.code,
        errorType: this.name,
        remaining: this.remaining,
        limit: this.limit,
        retryAfterMs: this.retryAfterMs,
      },
    };
  }
}
